import { useTenderActionError } from "../../../../../../../hooks/useTenderActionError";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import style from "./MobileSuggestPriceExpeditor.module.scss";
import { Expeditor } from "../../../../../../../services";
import CustomInput from "../../../../../../custom/custom-input/CustomInput";
import CustomInputCurrency from "../../../../../../custom/custom-input-currency/CustomInputCurrency";
import CustomButton from "../../../../../../custom/custom-button/CustomButton";
import SubmitBidModal from "../../../../../order-info-expeditor/components/SuggestPriceExpeditor/SubmitBidModal";
import ConfirmActionModal from "../../../../../order-info-expeditor/components/ConfirmActionModal/ConfirmActionModal";
import { TenderDeadlineText } from "../../../../../../shared/TenderDeadlineText/TenderDeadlineText";
import CustomTextarea from "../../../../../../custom/custom-textarea/CustomTextarea";
import { isTransitDaysRequired } from "../../../../../order-info-expeditor/utils/transitDaysValidation";

const MobileSuggestPriceExpeditor = ({ onRefreshOrder, order, cargoDetails, onUpdateData, onReject }) => {
  const { t } = useTranslation();
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState(order?.acceptedCurrency || "KZT");
  const lockCurrency = Boolean(order?.acceptedCurrency);
  const [volume, setVolume] = useState("");
  const [weight, setWeight] = useState("");
  const [transitDays, setTransitDays] = useState("");
  const [comments, setComments] = useState("");
  const [errorText, setErrorText] = useState("");
  const [transitDaysErrorText, setTransitDaysErrorText] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const initialVolume = useMemo(() => (cargoDetails?.volume != null ? String(cargoDetails.volume) : ""), [cargoDetails]);
  const initialWeight = useMemo(() => (cargoDetails?.weight != null ? String(cargoDetails.weight) : ""), [cargoDetails]);
  const weightUnit =
    cargoDetails?.unitMeasurement === "Kg"
      ? t("orderInfo.ordersInfoCargo.kgUnit")
      : t("orderInfo.ordersInfoCargo.tonsUnit");
  const transitDaysRequired = isTransitDaysRequired(order?.companyId);

  useEffect(() => {
    setPrice("");
    setVolume("");
    setWeight("");
    setTransitDays("");
    setComments("");
    setErrorText("");
    setTransitDaysErrorText("");
    setCurrency(order?.acceptedCurrency || "KZT");
  }, [order?.id, order?.acceptedCurrency]);

  const validate = () => {
    let isValid = true;
    const rounded = Math.round(Number(price));

    if (!rounded) {
      setErrorText(t("orderInfoExpeditor.suggestPrice.priceRequired"));
      isValid = false;
    } else if (rounded <= 0) {
      setErrorText(t("orderInfoExpeditor.suggestPrice.priceGreaterThanZero"));
      isValid = false;
    } else if (rounded > 2000000000) {
      setErrorText(t("orderInfoExpeditor.suggestPrice.priceTooLarge"));
      toast.error(t("orderInfoExpeditor.suggestPrice.priceTooLarge"));
      isValid = false;
    }

    if (transitDaysRequired && !transitDays.trim()) {
      setTransitDaysErrorText(t("orderInfoExpeditor.suggestPrice.transitDaysRequired"));
      isValid = false;
    }

    return isValid;
  };

  const handleSuggestClick = () => {
    if (!validate()) return;
    setShowConfirmModal(true);
  };

  const handleConfirmSend = async () => {
    if (!order?.id) return;
    setIsSubmitting(true);
    const rounded = Math.round(Number(price));
    const volumeValue = volume === "" ? null : Number(volume);
    const weightValue = weight === "" ? null : Number(weight);

    const data = {
      orderId: order.id,
      price: rounded,
      currency: currency,
      plannedDays: String(transitDays),
      comments: comments,
      volume: volumeValue,
      weight: weightValue,
    };

    try {
      await Expeditor.sendOrderBids(order.id, data);
      toast.success(t("orderInfoExpeditor.suggestPrice.bidSent"));
      setPrice("");
      setVolume("");
      setWeight("");
      setTransitDays("");
      setComments("");
      setShowConfirmModal(false);
      if (onUpdateData) onUpdateData();
    } catch (error) {
      console.error(error);
      await requestErrorDisplay(error, t, "orderInfoExpeditor.suggestPrice.sendBidError");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectClick = () => setShowRejectModal(true);

  const handleConfirmReject = async () => {
    if (!order?.id) return;
    setIsRejecting(true);
    try {
      await Expeditor.rejectTender(order.id);
      toast.success(t("orderInfoExpeditor.suggestPrice.rejectTenderSuccess"));
      setShowRejectModal(false);
      if (onReject) onReject();
      if (onUpdateData) onUpdateData();
    } catch {
      toast.error(t("orderInfoExpeditor.suggestPrice.rejectTenderError"));
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div className={style.card}>
      <div className={style.topbar}>
        <span className={style.topbar_title}>{t("orderInfoExpeditor.suggestPrice.title")}</span>
        {order?.tenderDateEnd && (
          <div className={style.tender_deadline}>
            <span className={style.tender_deadline_label}>{t("orderInfoExpeditor.suggestPrice.tenderDeadline")}</span>
            <span className={style.tender_deadline_value}><TenderDeadlineText date={order.tenderDateEnd} /></span>
          </div>
        )}
      </div>

      <div className={style.inputs_row}>
        <div className={style.input_price}>
          <CustomInputCurrency
            value={price}
            isBgGray
            inputType="number"
            labelText={`${t("orderInfo.suggestPrice.yourPriceLabel")}*`}
            placeholder={t("orderInfo.suggestPrice.placeholder")}
            errorText={errorText}
            currency={currency}
            lockCurrency={lockCurrency}
            onChangeValue={(value) => {
              setPrice(value);
              setErrorText("");
            }}
            onChangeCurrency={(currencyCode) => {
              setCurrency(currencyCode);
            }}
          />
        </div>
        <div className={style.input_secondary}>
          <CustomInput
            value={volume}
            isBgGray
            inputType="number"
            labelText={t("ordersCreateDesktop.detailsSection.volumeLabel")}
            placeholder={initialVolume ? `${initialVolume} ${t("orderInfoExpeditor.suggestPrice.cubicMeter")}` : t("orderInfoExpeditor.suggestPrice.cubicMeter")}
            onChangeValue={setVolume}
          />
        </div>
        <div className={style.input_secondary}>
          <CustomInput
            value={weight}
            isBgGray
            inputType="number"
            labelText={t("orderInfo.ordersInfoCargo.massLabel")}
            placeholder={initialWeight ? `${initialWeight} ${weightUnit}` : weightUnit}
            onChangeValue={setWeight}
          />
        </div>
      </div>
      {lockCurrency && (
        <span className={style.currency_hint}>{t("orderInfoExpeditor.suggestPrice.currencyLockedHint")}</span>
      )}
      <div className={style.inputs_row}>
        <CustomInput
          value={transitDays}
          isBgGray
          inputType="text"
          labelText={`${t("orderInfoExpeditor.suggestPrice.transitDaysLabel")}${transitDaysRequired ? "*" : ""}`}
          placeholder={t("orderInfoExpeditor.suggestPrice.transitDaysPlaceholder")}
          errorText={transitDaysErrorText}
          onChangeValue={(value) => {
            setTransitDays(value.replace(/[^0-9-]/g, ""));
            setTransitDaysErrorText("");
          }}
        />
      </div>
      <div className={style.inputs_row}>
        <CustomTextarea
          value={comments}
          isBgGray
          rows={1}
          labelText={t("orderInfo.suggestPrice.commentLabel")}
          placeholder={t("orderInfoExpeditor.suggestPrice.commentPlaceholder")}
          onChangeValue={setComments}
        />
      </div>

      <div className={style.buttons_row}>
        <div className={style.btn_primary}>
          <CustomButton
            text={t("orderInfo.suggestPrice.suggestButton")}
            isNavy
            onClick={handleSuggestClick}
            isLoading={isSubmitting}
            disabled={isSubmitting}
            style={{ width: "100%" }}
          />
        </div>
        <div className={style.btn_secondary}>
          <CustomButton
            text={t("orderInfoExpeditor.suggestPrice.reject")}
            isNavy
            isWhite
            onClick={handleRejectClick}
            disabled={isSubmitting}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <SubmitBidModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSend}
        isLoading={isSubmitting}
      />

      <ConfirmActionModal
        isOpen={showRejectModal}
        title={t("orderInfoExpeditor.suggestPrice.rejectTenderTitle")}
        message={t("orderInfoExpeditor.suggestPrice.rejectTenderMessage")}
        confirmText={t("orderInfoExpeditor.suggestPrice.rejectTenderConfirm")}
        onConfirm={handleConfirmReject}
        onCancel={() => setShowRejectModal(false)}
        isLoading={isRejecting}
      />
    </div>
  );
};

export default MobileSuggestPriceExpeditor;
